import type { ReactNode } from 'react';
import { Button } from './Button';
import { Skeleton } from './Skeleton';

/** The editor's frame while a board opens: paper-white dotted canvas, a top bar and a toolbar in outline (no raw text). */
export function BoardSkeleton({ label }: { label: string }) {
  return (
    <div role="status" aria-label={label} className="relative h-full overflow-hidden bg-paper [background-image:radial-gradient(circle,#d4d4d8_1px,transparent_1.2px)] [background-size:22px_22px]">
      <span aria-hidden="true" className="absolute left-3 top-3 flex h-11 w-64 items-center gap-2 rounded-xl bg-surface px-3 shadow-md">
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-4 w-32" />
      </span>
      <span aria-hidden="true" className="absolute bottom-4 left-1/2 flex h-13 w-[min(36rem,calc(100%-2rem))] -translate-x-1/2 items-center gap-2 rounded-xl bg-surface px-3 shadow-lg">
        {Array.from({ length: 9 }, (_, i) => (
          <Skeleton key={i} className="h-8 w-8 rounded-lg" />
        ))}
      </span>
    </div>
  );
}

/** A page of the dashboard while its data loads: a title and a grid of cards in outline. */
export function PageSkeleton({ label }: { label: string }) {
  return (
    <div role="status" aria-label={label} className="flex flex-col gap-4">
      <Skeleton className="h-7 w-48" />
      <div aria-hidden="true" className="grid grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-4">
        {Array.from({ length: 6 }, (_, i) => (
          <span key={i} className="flex flex-col overflow-hidden rounded-lg bg-surface shadow-sm">
            <Skeleton className="aspect-[16/10] w-full rounded-none" />
            <span className="flex flex-col gap-2 p-3">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/3" />
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

export function EmptyState({ icon, title, body, action }: { icon?: ReactNode; title: string; body?: string; action?: ReactNode }) {
  return (
    <div data-empty-state className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border px-6 py-10 text-center font-sans">
      {icon && <span className="text-fg-muted">{icon}</span>}
      <p className="text-sm font-medium text-fg">{title}</p>
      {body && <p className="max-w-sm text-sm text-fg-muted">{body}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function ErrorState({ title, body, onRetry, retryLabel, children }: { title: string; body?: string; onRetry?: () => void; retryLabel?: string; children?: ReactNode }) {
  return (
    <div className="mx-auto flex max-w-lg flex-col items-start gap-3 px-6 py-10 font-sans text-fg">
      {/* Only the message is announced; the actions follow it. */}
      <div role="alert" className="flex flex-col gap-1">
        <p className="font-display text-title font-semibold">{title}</p>
        {body && <p className="text-sm text-fg-muted">{body}</p>}
      </div>
      {(onRetry || children) && (
        <div className="flex flex-wrap gap-2">
          {onRetry && retryLabel && (
            <Button variant="primary" onClick={onRetry}>
              {retryLabel}
            </Button>
          )}
          {children}
        </div>
      )}
    </div>
  );
}
